import React, { useState, useEffect, useMemo } from 'react';
import {
  ShieldCheck,
  Search,
  CheckCircle2,
  XCircle,
  X,
  PlusCircle,
  Edit3,
  Trash2,
  Key,
  Users,
  Layers,
  RefreshCw,
  SlidersHorizontal,
  Shield,
  AlertTriangle,
} from 'lucide-react';
import {
  getRolesList,
  getRoleDetail,
  createRole,
  updateRole,
  updateRolePermissions,
  getPermissionsList,
  createPermission,
  updatePermission,
  deletePermission,
} from '../../api/rbac';
import type { Role, Permission } from '../../types/rbac';

export const RolesPermission: React.FC = () => {
  // Navigation tab state
  const [activeTab, setActiveTab] = useState<'roles' | 'permissions'>('roles');

  // Roles state
  const [roles, setRoles] = useState<Role[]>([]);
  const [loadingRoles, setLoadingRoles] = useState<boolean>(true);
  const [rolesError, setRolesError] = useState<string | null>(null);

  // System Permissions state
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loadingPermissions, setLoadingPermissions] = useState<boolean>(true);
  const [permissionsError, setPermissionsError] = useState<string | null>(null);

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [permSearchQuery, setPermSearchQuery] = useState('');

  // Modals state
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleForm, setRoleForm] = useState({ name: '', description: '' });
  const [roleFormError, setRoleFormError] = useState('');
  const [savingRole, setSavingRole] = useState(false);

  // Matrix Modal state
  const [isMatrixModalOpen, setIsMatrixModalOpen] = useState(false);
  const [matrixRole, setMatrixRole] = useState<Role | null>(null);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<number[]>([]);
  const [loadingMatrix, setLoadingMatrix] = useState(false);
  const [savingMatrix, setSavingMatrix] = useState(false);
  const [matrixSearch, setMatrixSearch] = useState('');

  // Permission CRUD Modal state
  const [isPermModalOpen, setIsPermModalOpen] = useState(false);
  const [editingPerm, setEditingPerm] = useState<Permission | null>(null);
  const [permForm, setPermForm] = useState({ permissionKey: '', description: '' });
  const [permFormError, setPermFormError] = useState('');
  const [savingPerm, setSavingPerm] = useState(false);
  const [permToDelete, setPermToDelete] = useState<Permission | null>(null);
  const [deletingPerm, setDeletingPerm] = useState(false);

  // Toast feedback state
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  // Fetch Roles
  const fetchRolesData = async () => {
    try {
      setLoadingRoles(true);
      setRolesError(null);
      const res = await getRolesList();
      if (res && res.data) {
        setRoles(Array.isArray(res.data) ? res.data : []);
      }
    } catch (err: any) {
      setRolesError(err.message || 'Failed to load roles');
      showToast('error', err.message || 'Failed to load roles');
    } finally {
      setLoadingRoles(false);
    }
  };

  // Fetch Permissions
  const fetchPermissionsData = async () => {
    try {
      setLoadingPermissions(true);
      setPermissionsError(null);
      const res = await getPermissionsList();
      if (res && res.data) {
        setPermissions(Array.isArray(res.data) ? res.data : []);
      }
    } catch (err: any) {
      setPermissionsError(err.message || 'Failed to load permissions');
    } finally {
      setLoadingPermissions(false);
    }
  };

  useEffect(() => {
    fetchRolesData();
    fetchPermissionsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtered Roles
  const filteredRoles = useMemo(() => {
    if (!searchQuery.trim()) return roles;
    const q = searchQuery.toLowerCase();
    return roles.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.description && r.description.toLowerCase().includes(q))
    );
  }, [roles, searchQuery]);

  // Filtered Permissions for Registry Tab
  const filteredPermissions = useMemo(() => {
    if (!permSearchQuery.trim()) return permissions;
    const q = permSearchQuery.toLowerCase();
    return permissions.filter(
      (p) =>
        p.permissionKey.toLowerCase().includes(q) ||
        (p.description && p.description.toLowerCase().includes(q))
    );
  }, [permissions, permSearchQuery]);

  // Grouped Permissions for Matrix
  const groupedPermissions = useMemo(() => {
    const groups: Record<string, Permission[]> = {};

    const sourcePermissions = permissions.filter((p) => {
      if (!matrixSearch.trim()) return true;
      const q = matrixSearch.toLowerCase();
      return (
        p.permissionKey.toLowerCase().includes(q) ||
        (p.description && p.description.toLowerCase().includes(q))
      );
    });

    sourcePermissions.forEach((p) => {
      const parts = p.permissionKey.split('.');
      const prefix = parts.length > 1 ? parts[0] : 'general';
      if (!groups[prefix]) {
        groups[prefix] = [];
      }
      groups[prefix].push(p);
    });

    return groups;
  }, [permissions, matrixSearch]);

  // Master Select All logic for Matrix
  const allMatrixPermIds = useMemo(() => {
    return permissions.map((p) => p.id);
  }, [permissions]);

  const isAllMatrixSelected = useMemo(() => {
    if (allMatrixPermIds.length === 0) return false;
    return allMatrixPermIds.every((id) => selectedPermissionIds.includes(id));
  }, [allMatrixPermIds, selectedPermissionIds]);

  const handleToggleMasterSelectAll = () => {
    if (isAllMatrixSelected) {
      setSelectedPermissionIds([]);
    } else {
      setSelectedPermissionIds([...allMatrixPermIds]);
    }
  };

  const handleToggleGroupSelectAll = (groupPerms: Permission[]) => {
    const groupIds = groupPerms.map((p) => p.id);
    const isAllGroupSelected = groupIds.every((id) => selectedPermissionIds.includes(id));

    if (isAllGroupSelected) {
      setSelectedPermissionIds((prev) => prev.filter((id) => !groupIds.includes(id)));
    } else {
      const newIds = new Set([...selectedPermissionIds, ...groupIds]);
      setSelectedPermissionIds(Array.from(newIds));
    }
  };

  const handleTogglePermission = (id: number) => {
    setSelectedPermissionIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Open Create/Edit Role Modal
  const handleOpenRoleModal = (role?: Role) => {
    if (role) {
      setEditingRole(role);
      setRoleForm({ name: role.name, description: role.description || '' });
    } else {
      setEditingRole(null);
      setRoleForm({ name: '', description: '' });
    }
    setRoleFormError('');
    setIsRoleModalOpen(true);
  };

  // Submit Create/Edit Role
  const handleSaveRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!roleForm.name.trim()) {
      setRoleFormError('Role Name is required');
      return;
    }

    try {
      setSavingRole(true);
      if (editingRole) {
        await updateRole({
          id: editingRole.id,
          name: roleForm.name,
          description: roleForm.description,
        });
        showToast('success', `Role "${editingRole.name}" updated successfully`);
      } else {
        await createRole({
          name: roleForm.name,
          description: roleForm.description,
        });
        showToast('success', 'New role created successfully');
      }
      setIsRoleModalOpen(false);
      fetchRolesData();
    } catch (err: any) {
      setRoleFormError(err.message || 'Failed to save role');
    } finally {
      setSavingRole(false);
    }
  };

  // Open Permission Matrix Modal
  const handleOpenMatrixModal = async (role: Role) => {
    setMatrixRole(role);
    setIsMatrixModalOpen(true);
    setLoadingMatrix(true);
    setMatrixSearch('');

    try {
      const res = await getRoleDetail(role.id);
      const detail = res.data;
      if (detail && detail.permissions) {
        const ids = detail.permissions.map((p: any) => (typeof p === 'object' ? p.id : p));
        setSelectedPermissionIds(ids);
      } else if (role.permissions) {
        const ids = role.permissions.map((p: any) => (typeof p === 'object' ? p.id : p));
        setSelectedPermissionIds(ids);
      } else {
        setSelectedPermissionIds([]);
      }
    } catch {
      if (role.permissions) {
        const ids = role.permissions.map((p: any) => (typeof p === 'object' ? p.id : p));
        setSelectedPermissionIds(ids);
      } else {
        setSelectedPermissionIds([]);
      }
    } finally {
      setLoadingMatrix(false);
    }
  };

  // Save Permission Matrix
  const handleSaveMatrix = async () => {
    if (!matrixRole) return;
    try {
      setSavingMatrix(true);
      await updateRolePermissions({
        id: matrixRole.id,
        permissionIds: selectedPermissionIds,
      });
      showToast('success', `Permissions updated for role "${matrixRole.name}"`);
      setIsMatrixModalOpen(false);
      fetchRolesData();
    } catch (err: any) {
      showToast('error', err.message || 'Failed to update permissions');
    } finally {
      setSavingMatrix(false);
    }
  };

  // Open Create/Edit Permission Modal
  const handleOpenPermModal = (perm?: Permission) => {
    if (perm) {
      setEditingPerm(perm);
      setPermForm({ permissionKey: perm.permissionKey, description: perm.description || '' });
    } else {
      setEditingPerm(null);
      setPermForm({ permissionKey: '', description: '' });
    }
    setPermFormError('');
    setIsPermModalOpen(true);
  };

  // Submit Create/Edit Permission
  const handleSavePerm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!permForm.permissionKey.trim()) {
      setPermFormError('Permission Key is required');
      return;
    }

    try {
      setSavingPerm(true);
      if (editingPerm) {
        await updatePermission({
          id: editingPerm.id,
          permissionKey: permForm.permissionKey,
          description: permForm.description,
        });
        showToast('success', 'Permission updated successfully');
      } else {
        await createPermission({
          permissionKey: permForm.permissionKey,
          description: permForm.description,
        });
        showToast('success', 'New permission key created');
      }
      setIsPermModalOpen(false);
      fetchPermissionsData();
    } catch (err: any) {
      setPermFormError(err.message || 'Failed to save permission');
    } finally {
      setSavingPerm(false);
    }
  };

  // Delete Permission
  const handleDeletePerm = async () => {
    if (!permToDelete) return;
    try {
      setDeletingPerm(true);
      await deletePermission(permToDelete.id);
      showToast('success', `Permission "${permToDelete.permissionKey}" deleted`);
      setPermToDelete(null);
      fetchPermissionsData();
    } catch (err: any) {
      showToast('error', err.message || 'Failed to delete permission');
    } finally {
      setDeletingPerm(false);
    }
  };

  // Metrics
  const totalRolesCount = roles.length;
  const totalPermissionsCount = permissions.length;

  return (
    <div className="space-y-6 min-h-screen">
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed top-5 right-5 z-50 px-4 py-3 rounded-xl shadow-xl border flex items-center gap-3 backdrop-blur-md transition-all duration-300 ${
            toast.type === 'success'
              ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
              : 'bg-rose-50 border-rose-300 text-rose-700'
          }`}
        >
          {toast.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 flex-shrink-0 text-emerald-600" />
          ) : (
            <XCircle className="w-5 h-5 flex-shrink-0 text-rose-600" />
          )}
          <span className="text-sm font-semibold">{toast.message}</span>
        </div>
      )}

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100">
            <ShieldCheck className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Role & Permission Control</h1>
            <p className="text-xs text-slate-500">
              Configure role access levels, authorization rules, and granular permission matrices
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              fetchRolesData();
              fetchPermissionsData();
            }}
            className="p-2.5 rounded-xl border border-slate-300 hover:bg-slate-100 text-slate-600 transition"
            title="Refresh Data"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          {activeTab === 'roles' ? (
            <button
              onClick={() => handleOpenRoleModal()}
              className="px-4 py-2.5 rounded-xl bg-[#C1272D] hover:bg-[#A81E23] text-white font-bold text-sm shadow-md flex items-center gap-2 transition"
            >
              <PlusCircle className="w-4 h-4" /> Create New Role
            </button>
          ) : (
            <button
              onClick={() => handleOpenPermModal()}
              className="px-4 py-2.5 rounded-xl bg-[#C1272D] hover:bg-[#A81E23] text-white font-bold text-sm shadow-md flex items-center gap-2 transition"
            >
              <PlusCircle className="w-4 h-4" /> Add Permission Key
            </button>
          )}
        </div>
      </div>

      {/* Metrics Banner */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="p-3 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100">
            <Shield className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">Configured Roles</p>
            <h3 className="text-2xl font-extrabold text-slate-900">{totalRolesCount}</h3>
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center gap-4">
          <div className="p-3 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <Key className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">System Permissions</p>
            <h3 className="text-2xl font-extrabold text-slate-900">{totalPermissionsCount}</h3>
          </div>
        </div>
      </div>

      {/* Tabs & Content Container */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Navigation Tabs */}
        <div className="flex border-b border-slate-200 bg-white px-4 pt-3">
          <button
            onClick={() => setActiveTab('roles')}
            className={`pb-3 px-4 font-bold text-sm transition-all border-b-2 flex items-center gap-2 ${
              activeTab === 'roles'
                ? 'border-[#C1272D] text-[#C1272D]'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <Shield className="w-4 h-4" /> Roles & Access Matrix
          </button>
          <button
            onClick={() => setActiveTab('permissions')}
            className={`pb-3 px-4 font-bold text-sm transition-all border-b-2 flex items-center gap-2 ${
              activeTab === 'permissions'
                ? 'border-[#C1272D] text-[#C1272D]'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <Key className="w-4 h-4" /> System Permissions Registry
          </button>
        </div>

        {/* TAB 1: ROLES & MATRIX */}
        {activeTab === 'roles' && (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search roles by name or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 rounded-xl bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:border-indigo-500 font-medium"
                />
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-slate-200">
              {loadingRoles ? (
                <div className="p-12 text-center text-slate-400 flex flex-col items-center gap-3">
                  <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
                  <p className="text-sm font-semibold">Loading roles...</p>
                </div>
              ) : rolesError ? (
                <div className="p-8 text-center text-rose-600 flex flex-col items-center gap-2">
                  <AlertTriangle className="w-6 h-6" />
                  <p className="text-sm font-bold">{rolesError}</p>
                  <button
                    onClick={fetchRolesData}
                    className="mt-2 text-xs font-bold underline hover:text-rose-700"
                  >
                    Retry Loading
                  </button>
                </div>
              ) : filteredRoles.length === 0 ? (
                <div className="p-12 text-center text-slate-400">
                  <p className="text-sm font-medium">No roles found matching your query.</p>
                </div>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#FFF8E1] text-slate-600 text-xs font-bold uppercase tracking-wider border-b border-slate-200">
                    <tr>
                      <th className="py-3.5 px-6">Role Name</th>
                      <th className="py-3.5 px-6">Description</th>
                      <th className="py-3.5 px-6 text-center">User Count</th>
                      <th className="py-3.5 px-6 text-center">Permission Count</th>
                      <th className="py-3.5 px-6 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {filteredRoles.map((role) => {
                      const userCount = role.userCount ?? role.roleCount ?? 0;
                      const permCount =
                        role.permissionCount ??
                        role.permission_count ??
                        (Array.isArray(role.permissions) ? role.permissions.length : 0);

                      return (
                        <tr key={role.id} className="hover:bg-slate-50 transition-colors">
                          <td className="py-4 px-6 font-bold text-slate-900">
                            <div className="flex items-center gap-2.5">
                              <span className="w-2.5 h-2.5 rounded-full bg-indigo-600"></span>
                              <span>{role.name}</span>
                            </div>
                          </td>
                          <td className="py-4 px-6 text-slate-600 text-xs sm:text-sm">
                            {role.description || <span className="italic text-slate-400">No description</span>}
                          </td>
                          <td className="py-4 px-6 text-center">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700">
                              <Users className="w-3.5 h-3.5 text-slate-500" />
                              {userCount} {userCount === 1 ? 'user' : 'users'}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-center">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                              <Key className="w-3.5 h-3.5 text-indigo-600" />
                              {permCount} permissions
                            </span>
                          </td>
                          <td className="py-4 px-6 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleOpenMatrixModal(role)}
                                className="px-3.5 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-bold flex items-center gap-1.5 transition"
                              >
                                <SlidersHorizontal className="w-3.5 h-3.5" /> Permissions
                              </button>
                              <button
                                onClick={() => handleOpenRoleModal(role)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
                                title="Edit Role Details"
                              >
                                <Edit3 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* TAB 2: SYSTEM PERMISSIONS REGISTRY */}
        {activeTab === 'permissions' && (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search permission keys..."
                  value={permSearchQuery}
                  onChange={(e) => setPermSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 rounded-xl bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:border-indigo-500 font-medium"
                />
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-slate-200">
              {loadingPermissions ? (
                <div className="p-12 text-center text-slate-400 flex flex-col items-center gap-3">
                  <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
                  <p className="text-sm font-semibold">Loading permission keys...</p>
                </div>
              ) : permissionsError ? (
                <div className="p-8 text-center text-rose-600">
                  <p className="text-sm font-bold">{permissionsError}</p>
                </div>
              ) : filteredPermissions.length === 0 ? (
                <div className="p-12 text-center text-slate-400">
                  <p className="text-sm font-medium">No permission keys found.</p>
                </div>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#FFF8E1] text-slate-600 text-xs font-bold uppercase tracking-wider border-b border-slate-200">
                    <tr>
                      <th className="py-3.5 px-6">ID</th>
                      <th className="py-3.5 px-6">Permission Key</th>
                      <th className="py-3.5 px-6">Description</th>
                      <th className="py-3.5 px-6 text-center">Associated Roles</th>
                      <th className="py-3.5 px-6 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {filteredPermissions.map((perm) => (
                      <tr key={perm.id} className="hover:bg-slate-50 transition-colors">
                        <td className="py-4 px-6 text-xs font-mono font-bold text-slate-400">
                          #{perm.id}
                        </td>
                        <td className="py-4 px-6 font-mono font-bold text-indigo-700 text-xs sm:text-sm">
                          {perm.permissionKey}
                        </td>
                        <td className="py-4 px-6 text-slate-600 text-xs sm:text-sm">
                          {perm.description || <span className="italic text-slate-400">No description</span>}
                        </td>
                        <td className="py-4 px-6 text-center">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700">
                            {perm.roleCount ?? 0} roles
                          </span>
                        </td>
                        <td className="py-4 px-6 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleOpenPermModal(perm)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
                              title="Edit Permission Key"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setPermToDelete(perm)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition"
                              title="Delete Permission Key"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>

      {/* MODAL 1: Create / Edit Role Modal */}
      {isRoleModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden p-6 space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <h3 className="text-lg font-bold text-slate-900">
                {editingRole ? 'Edit Role Details' : 'Create New Role'}
              </h3>
              <button
                onClick={() => setIsRoleModalOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveRole} className="space-y-4">
              {roleFormError && (
                <div className="p-3 rounded-xl bg-rose-50 text-rose-600 text-xs font-bold border border-rose-200">
                  {roleFormError}
                </div>
              )}

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Role Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. REGIONAL_SALES_MANAGER"
                  value={roleForm.name}
                  onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value.toUpperCase() })}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-sm font-bold text-slate-900 focus:outline-none focus:border-indigo-500"
                  required
                />
                <p className="text-[11px] text-slate-400 mt-1">Role names are automatically uppercased.</p>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Description
                </label>
                <textarea
                  rows={3}
                  placeholder="Describe access scope and responsibilities..."
                  value={roleForm.description}
                  onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-sm font-medium text-slate-900 focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setIsRoleModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-slate-600 text-sm font-semibold hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingRole}
                  className="px-5 py-2 rounded-xl bg-[#C1272D] hover:bg-[#A81E23] text-white text-sm font-bold shadow-md disabled:opacity-50 flex items-center gap-2"
                >
                  {savingRole && <RefreshCw className="w-4 h-4 animate-spin" />}
                  {editingRole ? 'Update Role' : 'Create Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: Interactive Permission Matrix Modal */}
      {isMatrixModalOpen && matrixRole && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-5 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold text-slate-900">Configure Permission Matrix</h3>
                  <span className="px-2.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-bold text-xs border border-indigo-200">
                    {matrixRole.name}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  Check permissions to assign or unassign from this role
                </p>
              </div>
              <button
                onClick={() => setIsMatrixModalOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Matrix Toolbar & Master Select All */}
            <div className="px-6 py-3 bg-slate-100 border-b border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="relative w-full sm:w-72">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Filter permissions..."
                  value={matrixSearch}
                  onChange={(e) => setMatrixSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 rounded-lg bg-white border border-slate-300 text-xs focus:outline-none focus:border-indigo-500 font-medium"
                />
              </div>

              <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end">
                <span className="text-xs text-slate-600 font-semibold">
                  Selected: <strong className="text-indigo-700">{selectedPermissionIds.length}</strong> / {permissions.length}
                </span>
                <button
                  type="button"
                  onClick={handleToggleMasterSelectAll}
                  className="px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-xs font-bold hover:bg-slate-50 transition flex items-center gap-1.5 text-slate-700"
                >
                  <CheckCircle2 className={`w-3.5 h-3.5 ${isAllMatrixSelected ? 'text-indigo-600' : 'text-slate-400'}`} />
                  {isAllMatrixSelected ? 'Deselect All' : 'Select All Master'}
                </button>
              </div>
            </div>

            {/* Matrix Content Body */}
            <div className="p-6 overflow-y-auto flex-1 space-y-6 bg-slate-50/30">
              {loadingMatrix ? (
                <div className="p-12 text-center text-slate-400 flex flex-col items-center gap-3">
                  <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
                  <p className="text-sm font-semibold">Loading role permission details...</p>
                </div>
              ) : Object.keys(groupedPermissions).length === 0 ? (
                <div className="p-12 text-center text-slate-400">
                  <p className="text-sm font-medium">No permission keys match your search criteria.</p>
                </div>
              ) : (
                Object.entries(groupedPermissions).map(([modulePrefix, groupPerms]) => {
                  const groupIds = groupPerms.map((p) => p.id);
                  const isGroupSelected = groupIds.every((id) => selectedPermissionIds.includes(id));

                  return (
                    <div
                      key={modulePrefix}
                      className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-xs"
                    >
                      {/* Module Header */}
                      <div className="px-4 py-3 bg-slate-100/70 flex items-center justify-between border-b border-slate-200">
                        <div className="flex items-center gap-2">
                          <Layers className="w-4 h-4 text-indigo-600" />
                          <span className="font-extrabold text-sm uppercase tracking-wider text-slate-800">
                            {modulePrefix}
                          </span>
                          <span className="text-xs text-slate-500 font-semibold">({groupPerms.length} keys)</span>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleToggleGroupSelectAll(groupPerms)}
                          className="text-xs font-bold text-indigo-600 hover:underline flex items-center gap-1"
                        >
                          {isGroupSelected ? 'Deselect Module' : 'Select All Module'}
                        </button>
                      </div>

                      {/* Permissions Grid */}
                      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {groupPerms.map((perm) => {
                          const isChecked = selectedPermissionIds.includes(perm.id);

                          return (
                            <label
                              key={perm.id}
                              onClick={() => handleTogglePermission(perm.id)}
                              className={`p-3 rounded-xl border transition-all cursor-pointer flex items-start gap-3 ${
                                isChecked
                                  ? 'bg-indigo-50/80 border-indigo-300 shadow-xs'
                                  : 'bg-white border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {}}
                                className="mt-0.5 rounded text-indigo-600 focus:ring-indigo-500 w-4 h-4"
                              />
                              <div className="space-y-0.5 flex-1 min-w-0">
                                <p className="font-mono text-xs font-bold text-slate-900 truncate">
                                  {perm.permissionKey}
                                </p>
                                <p className="text-[11px] text-slate-500 font-medium line-clamp-2">
                                  {perm.description || 'No description provided'}
                                </p>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Matrix Modal Footer */}
            <div className="p-4 border-t border-slate-200 bg-white flex items-center justify-between">
              <span className="text-xs text-slate-500 font-medium">
                Changes will be saved directly for role <strong>{matrixRole.name}</strong>.
              </span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setIsMatrixModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-slate-600 text-sm font-semibold hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveMatrix}
                  disabled={savingMatrix}
                  className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold shadow-md disabled:opacity-50 flex items-center gap-2"
                >
                  {savingMatrix && <RefreshCw className="w-4 h-4 animate-spin" />}
                  Save Permission Matrix
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 3: Create / Edit Permission Modal */}
      {isPermModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden p-6 space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <h3 className="text-lg font-bold text-slate-900">
                {editingPerm ? 'Edit Permission Key' : 'Add Permission Key'}
              </h3>
              <button
                onClick={() => setIsPermModalOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSavePerm} className="space-y-4">
              {permFormError && (
                <div className="p-3 rounded-xl bg-rose-50 text-rose-600 text-xs font-bold border border-rose-200">
                  {permFormError}
                </div>
              )}

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Permission Key <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. orders.create"
                  value={permForm.permissionKey}
                  onChange={(e) => setPermForm({ ...permForm, permissionKey: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-sm font-mono font-bold text-slate-900 focus:outline-none focus:border-indigo-500"
                  required
                />
                <p className="text-[11px] text-slate-400 mt-1">Format recommendation: `resource.action` (e.g. `users.create`).</p>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Description
                </label>
                <textarea
                  rows={3}
                  placeholder="Describe access scope..."
                  value={permForm.description}
                  onChange={(e) => setPermForm({ ...permForm, description: e.target.value })}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-300 text-sm font-medium text-slate-900 focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setIsPermModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-slate-600 text-sm font-semibold hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingPerm}
                  className="px-5 py-2 rounded-xl bg-[#C1272D] hover:bg-[#A81E23] text-white text-sm font-bold shadow-md disabled:opacity-50 flex items-center gap-2"
                >
                  {savingPerm && <RefreshCw className="w-4 h-4 animate-spin" />}
                  {editingPerm ? 'Update Key' : 'Create Key'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 4: Delete Permission Confirmation */}
      {permToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="w-full max-w-sm bg-white rounded-2xl border border-slate-200 shadow-2xl p-6 text-center space-y-4">
            <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-600 mx-auto flex items-center justify-center border border-rose-200">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">Delete Permission Key</h3>
              <p className="text-xs text-slate-500 mt-1">
                Are you sure you want to delete <strong className="font-mono text-slate-800">{permToDelete.permissionKey}</strong>? This cannot be undone.
              </p>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setPermToDelete(null)}
                className="flex-1 py-2 rounded-xl border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeletePerm}
                disabled={deletingPerm}
                className="flex-1 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deletingPerm && <RefreshCw className="w-4 h-4 animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesPermission;
